import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  Download,
  Users2,
  Languages,
  Target,
  Copy,
  Check,
  BookOpen,
  Users,
  ShieldCheck,
  Globe2,
} from "lucide-react";
import WovenPattern from "../components/WovenPattern";
import MultilingualWelcome from "../components/MultilingualWelcome";
import Reveal from "../components/Reveal";
import { STEPS } from "../data/steps";

const DIFFERENCES = [
  {
    icon: Target,
    title: "Beyond recruitment",
    body: "Many existing frameworks conceptualise inclusion as bringing diverse participants into research structures that have already been designed. MELT starts from a different premise: that the research process itself may need to change in response to linguistic, cultural and contextual diversity.",
  },
  {
    icon: Languages,
    title: "Beyond literal translation",
    body: "The challenges encountered across the four projects were rarely limited to word for word translation. They involved differences in cultural meaning, generational language use, interpretation of uncertainty, communication norms, and assumptions embedded within Anglo centric research practices.",
  },
  {
    icon: Users2,
    title: "Practical where inclusion succeeds or fails",
    body: "Existing tools state that diverse communities should be engaged. MELT addresses the more demanding question of how, down to the detail of scheduling, translator preparation, consent, and the interpretive weight carried by tone and local expression.",
  },
];

const AUDIENCE = [
  { icon: BookOpen, label: "Researchers and doctoral students conducting qualitative or participatory research" },
  { icon: Target, label: "Teams running research priority setting exercises" },
  { icon: Languages, label: "Researchers translating or culturally adapting questionnaires" },
  { icon: Users, label: "Patient and public involvement leads" },
  { icon: ShieldCheck, label: "Research ethics committees" },
  { icon: Globe2, label: "Community organisations partnering on research" },
];

const TIMELINE = [
  { n: "01", label: "Triangulation of data across four projects" },
  { n: "02", label: "Reflexive thematic analysis" },
  { n: "03", label: "Drafting of the guidance materials" },
  { n: "04", label: "Structured review by the Cochrane Priority Setting Methods Group" },
  { n: "05", label: "Iterative refinement after 2022–2023 conference presentations" },
];

const CITATION_TEXT =
  "Kumbargere Nagraj, S., Eachempati, P., Uhm, S. and Nasser, M. (2026) MELT: the MultiEthno-Linguistic Toolkit. Available at: https://melt-toolkit.com (Accessed: 21 September 2026).";

const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const patternY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="relative overflow-hidden woven-texture" data-testid="home-hero">
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-70"
        style={reduce ? undefined : { y: patternY }}
      >
        <WovenPattern opacity={0.16} />
      </motion.div>
      <div aria-hidden="true" className="pointer-events-none absolute -right-24 top-1/4 h-72 w-72 rounded-full bg-melt-amber/10 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-5 py-24 md:px-8 md:py-32">
        <h1 className="overflow-hidden font-serif text-6xl font-semibold leading-none text-[#FAF8F5] sm:text-7xl lg:text-8xl">
          <motion.span
            className="block tracking-tight"
            initial={reduce ? false : { y: "110%" }}
            animate={reduce ? {} : { y: "0%" }}
            transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          >
            MELT
          </motion.span>
        </h1>

        <motion.p
          className="mt-5 font-serif text-2xl text-melt-amber md:text-3xl"
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={reduce ? {} : { opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          data-testid="hero-subheadline"
        >
          The MultiEthno-Linguistic Toolkit
        </motion.p>

        <motion.p
          className="mt-6 max-w-2xl text-lg leading-relaxed text-[#FAF8F5]/85"
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={reduce ? {} : { opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          data-testid="hero-supporting"
        >
          Practical guidance for researchers working with multi ethnic and multi language communities.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap gap-4"
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={reduce ? {} : { opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.65 }}
        >
          <Link
            to="/toolkit"
            className="group inline-flex items-center gap-2 rounded-md bg-melt-teal px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-melt-teal-600"
            data-testid="hero-cta-toolkit"
          >
            Explore the toolkit
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </Link>
          <Link
            to="/toolkit#downloads"
            className="inline-flex items-center gap-2 rounded-md border border-[#FAF8F5]/30 px-6 py-3 text-base font-semibold text-[#FAF8F5] transition-colors hover:border-melt-amber hover:text-melt-amber"
            data-testid="hero-cta-download"
          >
            <Download size={18} aria-hidden="true" />
            Download all materials
          </Link>
        </motion.div>

        <div className="mt-20 border-t border-white/10 pt-10">
          <MultilingualWelcome tone="light" />
        </div>
      </div>
    </section>
  );
};

const CitationBox = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CITATION_TEXT);
      setCopied(true);
      toast.success("Citation copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Please copy the text manually.");
    }
  };
  return (
    <div className="rounded-lg border border-melt-grey bg-white p-8" data-testid="citation-box">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-serif text-lg italic text-melt-indigo/80">{CITATION_TEXT}</p>
        <button
          type="button"
          onClick={copy}
          className="inline-flex shrink-0 items-center gap-2 rounded-md border border-melt-teal px-4 py-2 text-sm font-semibold text-melt-teal transition-colors hover:bg-melt-teal hover:text-white"
          data-testid="copy-citation-button"
        >
          {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
          {copied ? "Copied" : "Copy citation"}
        </button>
      </div>
    </div>
  );
};

export default function Home() {
  return (
    <>
      <Hero />

      {/* What is MELT */}
      <section className="mx-auto max-w-4xl px-5 py-20 md:px-8 md:py-28" aria-labelledby="what-heading">
        <Reveal>
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-melt-teal">What is MELT</p>
          <h2 id="what-heading" className="mt-4 font-serif text-3xl font-semibold md:text-4xl">
            A seven step toolkit for inclusive research
          </h2>
          <p className="mt-6 text-lg text-melt-indigo/80">
            MELT is a seven step practical toolkit designed to support inclusive and co creative research
            with communities from diverse ethnic and language backgrounds. Under each step it provides
            checklists, templates, tip sheets and curated resources that address the linguistic, cultural
            and procedural adaptations required for genuinely inclusive research practice.
          </p>
          <p className="mt-4 text-lg text-melt-indigo/80">
            MELT was developed through an iterative and reflexive process across four research projects
            conducted in Malaysia and the United Kingdom, spanning research priority setting, participatory
            research methods, qualitative data collection, and questionnaire translation and cross cultural
            adaptation.
          </p>
        </Reveal>
      </section>

      {/* Why MELT is different */}
      <section className="woven-texture-light border-y border-melt-grey" aria-labelledby="diff-heading">
        <div className="mx-auto max-w-6xl px-5 py-20 md:px-8 md:py-24">
          <Reveal>
            <h2 id="diff-heading" className="font-serif text-3xl font-semibold md:text-4xl">Why MELT is different</h2>
          </Reveal>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {DIFFERENCES.map((d, i) => {
              const Icon = d.icon;
              return (
                <Reveal key={d.title} delay={i * 0.1}>
                  <article className="h-full rounded-lg border border-melt-grey bg-white p-8" data-testid={`diff-card-${i}`}>
                    <span className="flex h-12 w-12 items-center justify-center rounded-md bg-melt-teal/10 text-melt-teal">
                      <Icon size={24} aria-hidden="true" />
                    </span>
                    <h3 className="mt-5 font-serif text-xl font-semibold">{d.title}</h3>
                    <p className="mt-3 text-base text-melt-indigo/75">{d.body}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* The seven steps */}
      <section className="mx-auto max-w-6xl px-5 py-20 md:px-8 md:py-28" aria-labelledby="steps-heading">
        <Reveal>
          <h2 id="steps-heading" className="font-serif text-3xl font-semibold md:text-4xl">The seven steps</h2>
          <p className="mt-4 max-w-2xl text-lg text-melt-indigo/80">
            Each step carries its own checklists, templates and resources. Select a step to jump to its
            guidance on the Toolkit page.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 0.08}>
              <Link
                to={`/toolkit#${s.id}`}
                className="group flex h-full flex-col rounded-lg border border-melt-grey bg-white p-7 transition-all hover:-translate-y-1 hover:border-melt-teal hover:shadow-[0_16px_44px_rgba(46,42,91,0.1)]"
                data-testid={`step-card-${s.n}`}
              >
                <span className="font-serif text-5xl font-semibold leading-none text-melt-amber">{s.n}</span>
                <h3 className="mt-4 font-serif text-lg font-semibold text-melt-indigo">{s.name}</h3>
                <p className="mt-2 flex-1 text-base text-melt-indigo/75">{s.short}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-melt-teal">
                  Open step
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Who MELT is for */}
      <section className="woven-texture-light border-y border-melt-grey" aria-labelledby="audience-heading">
        <div className="mx-auto max-w-6xl px-5 py-20 md:px-8 md:py-24">
          <Reveal>
            <h2 id="audience-heading" className="font-serif text-3xl font-semibold md:text-4xl">Who MELT is for</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {AUDIENCE.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal key={i} delay={(i % 3) * 0.07}>
                  <div className="flex h-full items-start gap-4 rounded-lg border border-melt-grey bg-white p-6" data-testid={`audience-item-${i}`}>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-melt-amber/15 text-melt-amber">
                      <Icon size={20} aria-hidden="true" />
                    </span>
                    <p className="text-base leading-snug text-melt-indigo">{a.label}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* How MELT was developed */}
      <section className="mx-auto max-w-6xl px-5 py-20 md:px-8 md:py-28" aria-labelledby="dev-heading">
        <Reveal>
          <h2 id="dev-heading" className="font-serif text-3xl font-semibold md:text-4xl">How MELT was developed</h2>
        </Reveal>
        <ol className="mt-12 grid gap-6 md:grid-cols-5" data-testid="dev-timeline">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.n} delay={i * 0.08} as="li">
              <div className="relative h-full rounded-lg border border-melt-grey bg-white p-6">
                <span className="font-serif text-2xl font-semibold text-melt-amber">{t.n}</span>
                <p className="mt-3 text-[15px] leading-snug text-melt-indigo/80">{t.label}</p>
                {i < TIMELINE.length - 1 && (
                  <span aria-hidden="true" className="absolute -right-3 top-9 hidden text-melt-grey md:block">
                    <ArrowRight size={20} />
                  </span>
                )}
              </div>
            </Reveal>
          ))}
        </ol>
      </section>

      {/* Citing MELT */}
      <section className="mx-auto max-w-4xl px-5 pb-24 md:px-8" aria-labelledby="cite-heading">
        <Reveal>
          <h2 id="cite-heading" className="font-serif text-3xl font-semibold md:text-4xl">Citing MELT</h2>
          <p className="mt-4 text-lg text-melt-indigo/80">
            If you use MELT in your work, please cite the website as follows (Harvard style). Remember to
            update the access date to the day you consulted the toolkit.
          </p>
          <div className="mt-8">
            <CitationBox />
          </div>
        </Reveal>
      </section>

      {/* Closing CTA */}
      <section className="woven-texture" aria-labelledby="cta-heading">
        <div className="relative overflow-hidden">
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-60">
            <WovenPattern opacity={0.13} />
          </div>
          <div className="relative mx-auto max-w-4xl px-5 py-20 text-center md:px-8 md:py-24">
            <h2 id="cta-heading" className="font-serif text-3xl font-semibold text-[#FAF8F5] md:text-4xl">
              Use MELT in your research
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-[#FAF8F5]/85">
              MELT is a living resource. We welcome feedback and examples from your own research context.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/toolkit"
                className="group inline-flex items-center gap-2 rounded-md bg-melt-amber px-7 py-3.5 text-base font-semibold text-melt-indigo transition-transform hover:-translate-y-0.5"
                data-testid="cta-toolkit"
              >
                Download the toolkit
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-md border border-[#FAF8F5]/30 px-7 py-3.5 text-base font-semibold text-[#FAF8F5] transition-colors hover:border-melt-amber hover:text-melt-amber"
                data-testid="cta-contact"
              >
                Send feedback
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
